import type {
	QuestionnaireAnswerValue,
	QuestionnaireAnswers,
	QuestionnaireGender,
	QuestionnairePhase,
	QuestionnaireQuestion,
	QuestionnaireRiskLevel,
	QuestionnaireSource,
	QuestionnaireStructuredAnswer,
	QuestionnaireTemplate,
} from "../contracts"

export type QuestionnaireEligibilityFailure = {
	readonly questionId: string
	readonly question: string
	readonly selectedAnswer: string
	readonly riskLevel: QuestionnaireRiskLevel
	readonly source: QuestionnaireSource
}

export type QuestionnaireAnswerDraft = {
	readonly selectedAnswers: readonly string[]
	readonly details: string
}

export type QuestionnaireAnswerSummary = {
	readonly question: QuestionnaireQuestion
	readonly answer: string
	readonly details: string | null
	readonly riskLevel: QuestionnaireRiskLevel
}

export function isQuestionnaireStructuredAnswer(value: unknown): value is QuestionnaireStructuredAnswer {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false
	}
	if (!("value" in value)) {
		return false
	}
	if (
		typeof value.value !== "string" &&
		(!Array.isArray(value.value) || !value.value.every((answer) => typeof answer === "string"))
	) {
		return false
	}
	if ("details" in value && value.details !== undefined && typeof value.details !== "string") {
		return false
	}
	return Object.keys(value).every((key) => key === "value" || key === "details")
}

function parseQuestionnaireAnswerValue(value: unknown): QuestionnaireAnswerValue | null {
	if (typeof value === "string") {
		return value
	}
	if (Array.isArray(value)) {
		return value.every((answer) => typeof answer === "string") ? value : null
	}
	return isQuestionnaireStructuredAnswer(value) ? value : null
}

export function getQuestionnaireQuestionsForPhase(
	template: QuestionnaireTemplate,
	args: {
		readonly phase: QuestionnairePhase
		readonly gender?: QuestionnaireGender
	},
): readonly QuestionnaireQuestion[] {
	const questions: QuestionnaireQuestion[] = []
	for (const question of template.questions) {
		if (question.phase !== args.phase) {
			continue
		}
		if (args.gender !== undefined && question.gender !== "all" && question.gender !== args.gender) {
			continue
		}
		questions.push(question)
	}
	return questions
}

export function getSelectedQuestionnaireAnswers(value: unknown): readonly string[] {
	if (typeof value === "string") {
		const trimmed = value.trim()
		return trimmed.length === 0 ? [] : [trimmed]
	}
	if (Array.isArray(value)) {
		return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
	}
	if (isQuestionnaireStructuredAnswer(value)) {
		return getSelectedQuestionnaireAnswers(value.value)
	}
	return []
}

export function getQuestionnaireAnswerDetails(value: unknown): string | null {
	if (!isQuestionnaireStructuredAnswer(value)) {
		return null
	}
	const details = value.details
	if (typeof details !== "string") {
		return null
	}
	const trimmed = details.trim()
	return trimmed.length === 0 ? null : trimmed
}

export function questionnaireAnswerRequiresDetails(
	question: QuestionnaireQuestion,
	selectedAnswers: readonly string[],
): boolean {
	if (question.detailsRequiredForAnswers === undefined) {
		return false
	}
	for (const answer of selectedAnswers) {
		if (question.detailsRequiredForAnswers.includes(answer)) {
			return true
		}
	}
	return false
}

export function getQuestionnaireAnswerDraft(
	question: QuestionnaireQuestion,
	value: unknown,
): QuestionnaireAnswerDraft {
	const selectedAnswers = getSelectedQuestionnaireAnswers(value)
	if (question.type === "text") {
		const [answer] = selectedAnswers
		return {
			selectedAnswers,
			details: answer === undefined ? "" : answer,
		}
	}

	const details = getQuestionnaireAnswerDetails(value)
	return {
		selectedAnswers,
		details: details === null ? "" : details,
	}
}

export function buildQuestionnaireAnswerValue(args: {
	readonly question: QuestionnaireQuestion
	readonly selectedAnswers: readonly string[]
	readonly details: string
}): QuestionnaireAnswerValue | null {
	const trimmedDetails = args.details.trim()
	if (args.question.type === "text") {
		return trimmedDetails.length === 0 ? null : trimmedDetails
	}

	const selectedAnswers = args.selectedAnswers.filter((answer) => answer.trim().length > 0)
	if (selectedAnswers.length === 0) {
		return null
	}

	const value = args.question.type === "multiselect" ? selectedAnswers : selectedAnswers[0]
	if (value === undefined) {
		return null
	}

	if (args.question.detailsPrompt !== undefined && trimmedDetails.length > 0) {
		return { value, details: trimmedDetails }
	}

	return value
}

export function isQuestionnaireQuestionAnswered(
	question: QuestionnaireQuestion,
	answers: QuestionnaireAnswers | Record<string, unknown>,
): boolean {
	const value = answers[question.id]
	if (value === undefined) {
		return false
	}

	if (question.type === "text") {
		return getSelectedQuestionnaireAnswers(value).length > 0
	}

	const selectedAnswers = getSelectedQuestionnaireAnswers(value)
	if (selectedAnswers.length === 0) {
		return false
	}

	if (questionnaireAnswerRequiresDetails(question, selectedAnswers)) {
		return getQuestionnaireAnswerDetails(value) !== null
	}

	return true
}

export function isQuestionnairePhaseComplete(
	template: QuestionnaireTemplate,
	args: {
		readonly phase: QuestionnairePhase
		readonly gender?: QuestionnaireGender
		readonly answers: QuestionnaireAnswers | Record<string, unknown>
	},
): boolean {
	const questions = getQuestionnaireQuestionsForPhase(template, {
		phase: args.phase,
		...(args.gender === undefined ? {} : { gender: args.gender }),
	})
	if (questions.length === 0) {
		return args.phase === "pre_account"
	}
	for (const question of questions) {
		if (!isQuestionnaireQuestionAnswered(question, args.answers)) {
			return false
		}
	}
	return true
}

export function findFirstDisqualifyingQuestionnaireAnswer(
	template: QuestionnaireTemplate,
	args: {
		readonly gender: QuestionnaireGender
		readonly answers: QuestionnaireAnswers | Record<string, unknown>
	},
): QuestionnaireEligibilityFailure | null {
	const questions = getQuestionnaireQuestionsForPhase(template, {
		phase: "hard_eligibility",
		gender: args.gender,
	})
	for (const question of questions) {
		if (question.disqualifyingAnswers === undefined || question.disqualifyingAnswers.length === 0) {
			continue
		}
		const selectedAnswers = getSelectedQuestionnaireAnswers(args.answers[question.id])
		for (const selectedAnswer of selectedAnswers) {
			if (!question.disqualifyingAnswers.includes(selectedAnswer)) {
				continue
			}
			const answerMetadata = question.answerMetadata
			const metadata = answerMetadata === undefined ? undefined : answerMetadata[selectedAnswer]
			return {
				questionId: question.id,
				question: question.question,
				selectedAnswer,
				riskLevel: metadata === undefined ? "red" : metadata.riskLevel,
				source: metadata === undefined ? question.source : metadata.source,
			}
		}
	}
	return null
}

function resolveQuestionnaireAnswerRisk(
	question: QuestionnaireQuestion,
	selectedAnswers: readonly string[],
): QuestionnaireRiskLevel {
	let hasSelectedAnswerMetadata = false
	for (const answer of selectedAnswers) {
		const answerMetadata = question.answerMetadata
		const metadata = answerMetadata === undefined ? undefined : answerMetadata[answer]
		if (metadata !== undefined) {
			hasSelectedAnswerMetadata = true
		}
		if (metadata !== undefined && metadata.riskLevel === "red") {
			return "red"
		}
	}
	for (const answer of selectedAnswers) {
		const answerMetadata = question.answerMetadata
		const metadata = answerMetadata === undefined ? undefined : answerMetadata[answer]
		if (metadata !== undefined && metadata.riskLevel === "yellow") {
			return "yellow"
		}
	}
	if (hasSelectedAnswerMetadata) {
		return "regular"
	}
	return question.riskLevel
}

export function formatQuestionnaireAnswerValue(value: unknown): string {
	const selectedAnswers = getSelectedQuestionnaireAnswers(value)
	const details = getQuestionnaireAnswerDetails(value)
	if (selectedAnswers.length > 0 && details !== null) {
		return `${selectedAnswers.join(", ")} — ${details}`
	}
	if (selectedAnswers.length > 0) {
		return selectedAnswers.join(", ")
	}
	if (details !== null) {
		return details
	}
	return "—"
}

export function buildQuestionnaireAnswerSummaries(
	questions: readonly QuestionnaireQuestion[],
	answers: QuestionnaireAnswers | Record<string, unknown>,
): readonly QuestionnaireAnswerSummary[] {
	const summaries: QuestionnaireAnswerSummary[] = []
	for (const question of questions) {
		const value = answers[question.id]
		if (value === undefined) {
			continue
		}
		const selectedAnswers = getSelectedQuestionnaireAnswers(value)
		summaries.push({
			question,
			answer: formatQuestionnaireAnswerValue(value),
			details: question.type === "text" ? null : getQuestionnaireAnswerDetails(value),
			riskLevel: resolveQuestionnaireAnswerRisk(question, selectedAnswers),
		})
	}
	return summaries
}

function getNoToAllAnswerForQuestion(question: QuestionnaireQuestion): QuestionnaireAnswerValue {
	for (const option of question.options) {
		const answerMetadata = question.answerMetadata
		const metadata = answerMetadata === undefined ? undefined : answerMetadata[option]
		if (metadata !== undefined && metadata.riskLevel !== "regular") {
			continue
		}
		const normalized = option.toLowerCase()
		if (normalized.startsWith("no") || normalized.includes("none of")) {
			return question.type === "multiselect" ? [option] : option
		}
	}
	throw new Error(`Hard eligibility question has no safe no-to-all answer: ${question.id}`)
}

export function buildHardEligibilityNoToAllAnswers(
	template: QuestionnaireTemplate,
	args: {
		readonly gender: QuestionnaireGender
		readonly existingAnswers?: QuestionnaireAnswers | Record<string, unknown>
	},
): QuestionnaireAnswers {
	const answers: Record<string, QuestionnaireAnswerValue> = {}
	const hardQuestionIds = new Set(
		template.questions
			.filter((question) => question.phase === "hard_eligibility")
			.map((question) => question.id),
	)
	const existingAnswers = args.existingAnswers
	if (existingAnswers !== undefined) {
		for (const [questionId, answer] of Object.entries(existingAnswers)) {
			if (hardQuestionIds.has(questionId)) {
				continue
			}
			const parsed = parseQuestionnaireAnswerValue(answer)
			if (parsed !== null) {
				answers[questionId] = parsed
			}
		}
	}

	const hardQuestions = getQuestionnaireQuestionsForPhase(template, {
		phase: "hard_eligibility",
		gender: args.gender,
	})
	for (const question of hardQuestions) {
		answers[question.id] = getNoToAllAnswerForQuestion(question)
	}
	return answers
}

function normalizeAnswerValueForSignature(value: unknown): unknown {
	const parsed = parseQuestionnaireAnswerValue(value)
	if (parsed === null) {
		return null
	}
	if (typeof parsed === "string") {
		return parsed
	}
	if (Array.isArray(parsed)) {
		return [...parsed].sort()
	}
	const normalizedValue = Array.isArray(parsed.value)
		? [...parsed.value].sort()
		: parsed.value
	return {
		value: normalizedValue,
		...(parsed.details === undefined ? {} : { details: parsed.details.trim() }),
	}
}

export function buildHardEligibilityAnswerSignature(
	template: QuestionnaireTemplate,
	args: {
		readonly gender: QuestionnaireGender
		readonly answers: QuestionnaireAnswers | Record<string, unknown>
	},
): string {
	const hardQuestions = getQuestionnaireQuestionsForPhase(template, {
		phase: "hard_eligibility",
		gender: args.gender,
	})
	return JSON.stringify(
		hardQuestions.map((question) => [
			question.id,
			normalizeAnswerValueForSignature(args.answers[question.id]),
		]),
	)
}

export function buildAggregateHardEligibilityFailure(
	template: QuestionnaireTemplate,
	args: {
		readonly gender: QuestionnaireGender
	},
): QuestionnaireEligibilityFailure {
	const [firstQuestion] = getQuestionnaireQuestionsForPhase(template, {
		phase: "hard_eligibility",
		gender: args.gender,
	})
	if (firstQuestion === undefined) {
		throw new Error("Hard eligibility template has no screening questions")
	}
	return {
		questionId: "hard_eligibility_any",
		question: "Do any of the listed hard-disqualifier conditions apply to you?",
		selectedAnswer: "Yes — one or more listed hard-disqualifier conditions apply",
		riskLevel: "red",
		source: firstQuestion.source,
	}
}
